import React, { useEffect, useState } from 'react';
import { Tabs } from 'antd';
import type { Company } from '../../types/company';
import { BANK_TRANSFER_STORAGE_KEY } from './bankTransferConstants';
import { BaseContractPanel } from './BaseContractPanel';
import { AddonManagementPanel } from './AddonManagementPanel';
import { CancellationPanel } from '../subscription/CancellationPanel';
import InvoicesPanel from './InvoicesPanel';
import { useCompanyState } from '../../hooks/useCompanyState';
import { useHashTab } from '../../hooks/useHashTab';

interface SubscriptionTabProps {
  company?: Company;
  isManager: boolean;
  currentUser?: {
    id: string;
    email?: string | null;
    name?: string | null;
    role?: string | null;
  };
}

const SUBSCRIPTION_TAB_KEYS = ['contract', 'addons', 'invoices', 'cancellation'] as const;
type SubscriptionTabKey = typeof SUBSCRIPTION_TAB_KEYS[number];

const SubscriptionTab: React.FC<SubscriptionTabProps> = ({ company, isManager, currentUser }) => {
  const [hasStoredBankTransferResult, setHasStoredBankTransferResult] = useState(false);
  const [bankTransferLastSavedAt, setBankTransferLastSavedAt] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useHashTab<SubscriptionTabKey>('contract', SUBSCRIPTION_TAB_KEYS);

  // 会社状態を取得（v2.1）
  const companyState = useCompanyState(company?.id);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      const raw = sessionStorage.getItem(BANK_TRANSFER_STORAGE_KEY);
      if (!raw) {
        setHasStoredBankTransferResult(false);
        setBankTransferLastSavedAt(null);
        return;
      }
      const parsed = JSON.parse(raw) as {
        savedAt?: string;
        result?: { bankTransferInstructions?: unknown };
      } | null;
      if (parsed?.result?.bankTransferInstructions) {
        setHasStoredBankTransferResult(true);
        setBankTransferLastSavedAt(parsed.savedAt ?? null);
      } else {
        setHasStoredBankTransferResult(false);
        setBankTransferLastSavedAt(null);
      }
    } catch (error) {
      console.warn('[SubscriptionTab] Failed to restore cached bank transfer info', error);
    }
  }, [company?.id]);

  const handleTabChange = (key: string) => {
    if (SUBSCRIPTION_TAB_KEYS.includes(key as SubscriptionTabKey)) {
      setActiveTab(key as SubscriptionTabKey);
    }
  };

  // 子コンポーネントからのタブ切り替え用（string型を受け取る）
  const handleSwitchToTab = (tabKey: string) => {
    if (SUBSCRIPTION_TAB_KEYS.includes(tabKey as SubscriptionTabKey)) {
      setActiveTab(tabKey as SubscriptionTabKey);
    }
  };

  if (!company) {
    return <div>会社情報を読み込み中...</div>;
  }

  const tabItems = [
    {
      key: 'contract',
      label: '基本契約',
      children: (
        <BaseContractPanel
          company={company}
          isManager={isManager}
          currentUser={currentUser}
          hasStoredBankTransferResult={hasStoredBankTransferResult}
          bankTransferLastSavedAt={bankTransferLastSavedAt}
          onSwitchToTab={handleSwitchToTab}
        />
      ),
    },
    {
      key: 'addons',
      label: '追加オプション',
      children: (
        <AddonManagementPanel
          company={company}
          isManager={isManager}
          onSwitchToTab={handleSwitchToTab}  // [v2.1] 追加
        />
      ),
    },
    {
      key: 'invoices',
      label: '請求書・領収書',
      children: <InvoicesPanel company={company} />,
    },
    {
      key: 'cancellation',
      label: '解約',
      children: (
        <CancellationPanel
          company={company}
          companyState={companyState}
          onUpdate={() => {
            // データを再取得
            window.location.reload();
          }}
        />
      ),
    },
  ];

  return (
    <div>
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        size="large"
      />
    </div>
  );
};

export default SubscriptionTab;
